import { useState } from 'react'
import InsightCellsTable from '../benchmarkDetails/InsightCellsTable'
import { BENCHMARK_STATE, publicationBlockedReason } from '../../helpers/intelligence/effectBenchmarkGate'
import telwoord from '../../helpers/intelligence/telwoord'
import { ISS_CLASSIFICATION_LABELS } from '../../helpers/sectorInsights/issClassifications'

/**
 * @typedef {object} AgendaRowData
 * @property {string} cellId - Stabiele celsleutel
 * @property {string} effectName - Naam van het MSI-effect
 * @property {string} dimensionLabel - Waar de cel over gaat (mechanisme, doelgroep, …)
 * @property {number} nOrgs - Bijdragende organisaties nu
 * @property {number} nRespondents - Respondenten nu
 * @property {number} needsOrgs - Organisaties tekort tot de poort
 * @property {number} needsRespondents - Respondenten tekort tot de poort
 * @property {number} readiness - 0–1, de laagste van beide kolommen
 * @property {number|null} maxOrgShare - Aandeel van de grootste organisatie (0–1)
 * @property {{name: string, respondents: number}|null} topContributor - Grootste bijdrager
 * @property {string|null} [segment] - Doelgroep waar de cel op inzoomt; leeg = ongesegmenteerd
 * @property {string} [segmentLabel] - Leesbare naam van die doelgroep
 * @property {boolean} publishable - P2 gehaald én een toonbare classificatie (zoals de server publiceren toestaat)
 * @property {('p2'|'classificatie'|null)} [blockedBy] - Waarom niet publiceerbaar; null als het wel kan
 * @property {({classification?: string}|object|null)} [cell] - Het celdocument zelf; draagt de classificatie en de onderbouwing achter de regel
 */

// Boven dit aandeel scoort de spreidingskolom van P2 niets meer; dan is de cel
// in de praktijk één organisatie.
const DOMINANT_VANAF = 0.5

/**
 * Wat er nog ontbreekt, als zin. Alleen de kolom(men) die werkelijk tekortkomen
 * worden genoemd — "nog 0 organisaties" leest als werk dat er niet is.
 * @param {number} orgs @param {number} respondenten @returns {string}
 */
const watOntbreekt = (orgs, respondenten) => {
    const delen = []
    if (orgs > 0) delen.push(telwoord(orgs, 'organisatie', 'organisaties'))
    if (respondenten > 0) delen.push(telwoord(respondenten, 'respondent', 'respondenten'))
    // Genoeg organisaties én respondenten, toch niet publiceerbaar: dan houdt
    // de derde kolom van P2 hem tegen — één organisatie levert te veel van de
    // metingen. 'Nog  tot publiceerbaar.' zou een lege zin zijn.
    if (!delen.length) return 'De spreiding over organisaties houdt de poort nog tegen.'
    return `Nog ${delen.join(' en ')} tot publiceerbaar.`
}

/**
 * Wie de cel domineert, als zin — of niets als de spreiding gezond is of we de
 * bijdrager niet kennen.
 * @param {number|null} share @param {{name: string}|null} top @returns {string}
 */
const dominantie = (share, top) => {
    if (typeof share !== 'number' || !top || !top.name) return ''
    if (share < DOMINANT_VANAF) return ''
    // Bij 100% zegt het percentage minder dan de betekenis: dit is geen
    // aggregaat maar de data van één organisatie.
    if (share >= 1) return `${top.name} levert alle metingen.`
    return `${top.name} levert ${Math.round(share * 100)}% van de metingen.`
}

/**
 * Eén regel op de Agenda: een inzicht-cel met de afstand tot publiceerbaar.
 *
 * De regel is bewust geformuleerd als doel en niet als score. "Nog 5
 * organisaties" is stuurbaar; "gereedheid 28%" is dat niet.
 *
 * De regel zegt hoe VER de cel van publiceerbaar af staat. Waaróp die stand
 * rust -- classificatie, meetdesign, bewijskracht B, effectgrootte E,
 * meetronden, replicatie -- staat achter 'Onderbouwing'. Dat is geen luxe:
 * hiervandaan open je het celdetail, en dan hoor je de cijfers te kunnen
 * nakijken in plaats van ze op hun woord te geloven. De tabel rendert recht uit
 * het scoredocument, nooit uit een redactietekst (ISS-curatiewaarborg b).
 *
 * Dicht bij binnenkomst: de agenda is een lijst om in te kiezen, geen stapel
 * tabellen. De cellen zijn al opgehaald door useAgenda, dus uitklappen kost
 * geen tweede aanroep.
 *
 * AI-CONTEXT: See docs/features/insight-strength-score-2.md
 *
 * @param {object} props
 * @param {AgendaRowData} props.row - De agendaregel
 * @param {boolean|null} [props.alBuiten] - Staat deze regel al gepubliceerd; null = nog niet bekend
 * @param {((row: AgendaRowData) => void)|null} [props.onOpenCell] - Opent het celdetail van deze regel; zonder handler of zonder celdocument geen knop
 * @param {((cel: object) => void)|null} [props.onClassificationClick] - Maakt de classificatie in de onderbouwing aanklikbaar (opent de uitleg-popup); zonder handler niet aanklikbaar
 * @param {boolean} [props.showSegment] - Toon 'Alleen gemeten bij: …'; uit op het themadetail, waar de doelgroep al in de kop staat
 * @param {string} [props.benchmarkState] - De stand van de benchmark van het effect (BENCHMARK_STATE); weglaten = onbekend, en dan is de cel niet klaar
 * @param {object|null} [props.p2Targets] - De P2-drempels van de server, door naar de onderbouwingstabel. Zonder dit rekent die tabel met zijn eigen kopie, en dan kan de zin op de regel een andere afstand noemen dan de tabel eronder.
 */
const AgendaRow = ({
    row, alBuiten = null, onOpenCell = null, onClassificationClick = null, showSegment = true,
    benchmarkState = BENCHMARK_STATE.unknown, p2Targets = null,
}) => {
    const [onderbouwingOpen, setOnderbouwingOpen] = useState(false)

    return (
    <div className="intelligence-agenda-row">
        <div className="intelligence-agenda-row__body">
            {/* Oudere celdocumenten dragen nog geen effectnaam; dan alleen de
                dimensie, zonder losse scheider ervoor. */}
            <p className="intelligence-agenda-row__title">
                {row.effectName ? `${row.effectName} · ` : ''}{row.dimensionLabel}
            </p>
            <span className="intelligence-agenda-row__stand">
                {telwoord(row.nOrgs, 'organisatie', 'organisaties')}, {telwoord(row.nRespondents, 'respondent', 'respondenten')}
            </span>
            {/* Zonder deze regel staan er twee regels voor hetzelfde effect en
                dezelfde dimensie met verschillende aantallen, en dat leest als
                een rekenfout in plaats van als twee uitspraken. 'Gemeten bij'
                en niet 'over': het zijn de antwoorden van die doelgroep zelf.
                De poort blijft dezelfde — een segmentcel krijgt geen eigen
                drempel, alleen een leesbare afbakening. */}
            {/* Op het themadetail staat de doelgroep al in de kop, dus daar is
                deze regel ruis (showSegment=false). Elders blijft hij: zonder
                lezen een segmentcel en een effectbrede cel als één rij. */}
            {showSegment && row.segmentLabel && (
                <span className="intelligence-agenda-row__segment">
                    Alleen gemeten bij: {row.segmentLabel}
                </span>
            )}
            {dominantie(row.maxOrgShare, row.topContributor) && (
                <span className="intelligence-agenda-row__dominant">
                    {dominantie(row.maxOrgShare, row.topContributor)}
                </span>
            )}
            {/* Twee poorten. P2 gaat over deze cel; de benchmark over het
                effect waar de cel een plak van is. De dichtstbijzijnde poort
                staat vooraan: wie P2 nog niet haalt heeft aan 'wacht op de
                benchmark' niets. Haalt de cel P2 en de benchmark niet, dan is
                'klaar om te publiceren' onwaar — de server weigert hem
                (ADR-040).
                AI-CONTEXT: See ../../helpers/intelligence/effectBenchmarkGate.js */}
            {!row.publishable ? (
                <span className="intelligence-agenda-row__gap">
                    {row.blockedBy === 'classificatie'
                        ? `Niet toonbaar: ${ISS_CLASSIFICATION_LABELS[row.cell?.classification] || 'nog geen classificatie'}.`
                        : watOntbreekt(row.needsOrgs, row.needsRespondents)}
                </span>
            ) : benchmarkState === BENCHMARK_STATE.ok ? (
                // AI-CONTEXT: See docs/pitfalls.md#ui-independent-badges-contradict-each-other
                // Niet 'klaar om te publiceren' zetten naast 'staat al buiten':
                // klaar om te doen wat al gedaan is. Wat een cel nog MIST blijft
                // hierboven wél staan als hij al buiten staat — gepubliceerd en
                // inmiddels onder de poort gezakt is juist het geval dat iemand
                // moet zien.
                alBuiten !== true && (
                    <span className="intelligence-badge intelligence-badge--ready">
                        klaar om te publiceren
                    </span>
                )
            ) : (
                <span className="intelligence-agenda-row__gap">
                    {publicationBlockedReason(benchmarkState)}
                </span>
            )}
        </div>
        {alBuiten === true && (
            <span className="intelligence-badge">staat al buiten</span>
        )}
        {/* Elke regel met een celdocument heeft een detailpagina: de cijfers,
            per organisatie, de mechanismen erachter en de interviews. Juist
            als de cel de poort nog niet haalt is dat de plek om te zien
            waarom (besluit user 2026-09-14: knop op elke regel). Het inzicht
            (concept of publicatie) staat dáár, niet meer hier. */}
        {row.cell && onOpenCell && (
            <button
                type="button"
                className="intelligence-row-action"
                onClick={() => onOpenCell(row)}
            >
                Bekijk details
            </button>
        )}
        {/* Zonder celdocument valt er niets te onderbouwen, en dan hoort er
            ook geen knop te staan die dat belooft. */}
        {row.cell && (
            <button
                type="button"
                className="intelligence-row-action intelligence-row-action--stil"
                aria-expanded={onderbouwingOpen}
                onClick={() => setOnderbouwingOpen((open) => !open)}
            >
                {onderbouwingOpen ? 'Onderbouwing verbergen' : 'Onderbouwing'}
            </button>
        )}
        {/* <progress> in plaats van een gestileerde balk: een breedte per regel
            zou een inline style vragen, en die zijn hier niet toegestaan. */}
        <progress
            className="intelligence-agenda-row__meter"
            value={row.readiness}
            max={1}
            aria-label={`Voortgang naar publiceerbaar voor ${row.effectName}`}
        />
        {/* Eén cel, één rij: dit is de onderbouwing van DEZE regel. De hele
            tabel van het effect zou onder elke regel van dat effect opnieuw
            staan. showNextTierGaps zet per kolom wat er nog aan ontbreekt --
            fijner dan de zin op de regel, die alleen de bindende kolom noemt. */}
        {onderbouwingOpen && row.cell && (
            <div className="intelligence-agenda-row__onderbouwing">
                <InsightCellsTable
                    cells={[row.cell]}
                    showNextTierGaps
                    p2Targets={p2Targets}
                    onClassificationClick={onClassificationClick || undefined}
                />
            </div>
        )}
    </div>
    )
}

export default AgendaRow
